import React from 'react';
import classNames from 'classnames';
import { useTheme } from '@components/theme';
import PrismicImage from '@components/shared/images/atoms/Image/PrismicImage';
import breakpoints from '@constants/breakpoints';
import { useMaxBreakpoint } from '@hooks/useMediaQuery/useMediaQuery';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import styles from './FeaturedItem.module.scss';
import { IFeaturedItem } from '../../model/ShopPage';

interface IFeaturedItemProps {
  item: IFeaturedItem;
  imagePosition?: 'left' | 'right';
  className?: string;
  style?: Record<string, number|string>;
}

const FeaturedText = ({
  item,
}: IFeaturedItemProps) => {
  const theme = useTheme();

  const isMobile = useMaxBreakpoint('small-tablet');

  return (
    <div className={styles.FeaturedText}>
      <h3 className={classNames(
        styles.FeaturedTitle,
        theme.styles.Header,
        {
          [theme.styles.Core2]: isMobile,
        }
      )}
      >{item.title}
      </h3>
      {item.description && (
      <p className={styles.FeaturedDescription}>{item.description}</p>
      )}
      {item.cta && (
      <a
        className={classNames(
          theme.styles.Button,
          theme.styles.Small,
          {
            [theme.styles.Conflicting]: isMobile,
            [theme.styles.Primary]: !isMobile,
          }
        )}
        role="button"
        href={item.cta.url}
        onClick={() => {
          analyticsTrack(
            `cta_clicked`, {
              title: `Shop Category Page | Featured Item`,
              featureItem: {
                title: item.title,
                description: item.description,
                image: item.image
              },
              ctaLabel: item?.cta?.text
            }
          );
        }}
      >
        {item.cta.text}
      </a>
      )}
    </div>
  );
};

const FeaturedItem = ({
  className = '',
  item,
  imagePosition = 'left',
  style,
}: IFeaturedItemProps) => {
  const theme = useTheme();
  return (
    <div
      className={classNames(
        className,
        styles.FeaturedItem, {
          [styles.ImageLeft]: imagePosition === 'left',
          [styles.ImageRight]: imagePosition === 'right'
        }
      )}
      style={style}
    >
      { item.image &&
        (
          <div className={styles.FeaturedImage}>
            <PrismicImage
              image={item.image}
              breakpoints={{
                mobile: breakpoints.tablet - 1
              }}
            />
            <div className={classNames(styles.Overlay, theme.styles.Overlay)} />
          </div>
        )}
      <div className={styles.FeaturedTextWrapper}>
        <FeaturedText item={item} />
      </div>
    </div>
  );
};

export default FeaturedItem;
